/* ============================================================
   BGC — bgc-utility.js
   Interaction for the five utility sections owned by AGENT-BASE:
   bgc-product-main, bgc-collection-grid, bgc-page, bgc-cart, bgc-404.
   Guards on element existence. Uses window.BGC from bgc-core.js.
   ============================================================ */

(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    const BGC = window.BGC || {};

    /* ---------- generic qty steppers (product page) ---------- */
    document.querySelectorAll(".qty[data-cart-line]:not([data-bgc-qty]), .qty:not([data-bgc-qty])").forEach((q) => {
      if (q.closest("[data-bgc-qty]")) return;
      const out = q.querySelector("[data-pdp-qty], span");
      if (!out) return;
      let n = parseInt(out.textContent, 10) || 1;
      q.querySelectorAll("button").forEach((b) =>
        b.addEventListener("click", () => {
          n = Math.max(1, Math.min(9, n + (b.dataset.d === "+" ? 1 : -1)));
          out.textContent = n;
        })
      );
    });

    /* ---------- PDP: swatch picker ---------- */
    const pdpSwatches = document.querySelectorAll("[data-pdp-swatch]");
    if (pdpSwatches.length) {
      const addBtn = document.querySelector("[data-pdp-add]");
      const priceEl = document.querySelector("[data-pdp-price]");
      const mainImgs = document.querySelectorAll("[data-pdp-img]");
      const colorNameEl = document.querySelector("[data-pdp-color-name]");

      pdpSwatches.forEach((sw) => {
        sw.addEventListener("click", () => {
          pdpSwatches.forEach((s) => s.classList.remove("on"));
          sw.classList.add("on");
          if (addBtn && sw.dataset.variantId) addBtn.dataset.variantId = sw.dataset.variantId;
          if (priceEl && sw.dataset.price) priceEl.textContent = sw.dataset.price;
          if (colorNameEl && sw.dataset.color) colorNameEl.textContent = sw.dataset.color;
          if (sw.dataset.img) {
            let matched = false;
            mainImgs.forEach((im) => {
              const isMatch = im.getAttribute("src") === sw.dataset.img;
              im.classList.toggle("on", isMatch);
              if (isMatch) matched = true;
            });
            if (!matched && mainImgs[0]) {
              mainImgs.forEach((im) => im.classList.remove("on"));
              mainImgs[0].src = sw.dataset.img;
              mainImgs[0].classList.add("on");
            }
          }
        });
      });
    }

    /* ---------- PDP: add to cart ---------- */
    const pdpAdd = document.querySelector("[data-pdp-add]");
    if (pdpAdd && BGC.addToCart) {
      pdpAdd.addEventListener("click", () => {
        const variantId = pdpAdd.dataset.variantId;
        if (!variantId) return;
        const qtyEl = document.querySelector("[data-pdp-qty]");
        const qty = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;
        pdpAdd.disabled = true;
        BGC.addToCart(variantId, qty).then(() => { pdpAdd.disabled = false; });
      });
    }

    /* ---------- collection grid: add to cart ---------- */
    if (BGC.addToCart) {
      document.querySelectorAll("[data-bgc-add]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const variantId = btn.dataset.variantId;
          if (!variantId) return;
          btn.disabled = true;
          BGC.addToCart(variantId, 1).then(() => { btn.disabled = false; });
        });
      });
    }

    /* ---------- collection grid: color swatches (image swap + variant sync) ---------- */
    document.querySelectorAll(".prod-swatch-row").forEach((row) => {
      const card = row.closest(".prod-card");
      if (!card) return;
      const img = card.querySelector("[data-prod-card-img]");
      const addBtn = card.querySelector("[data-bgc-add]");
      const dots = row.querySelectorAll("[data-prod-swatch]");

      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          if (dot.classList.contains("is-selected")) return;

          dots.forEach((d) => {
            d.classList.remove("is-selected");
            d.setAttribute("aria-pressed", "false");
          });
          dot.classList.add("is-selected");
          dot.setAttribute("aria-pressed", "true");

          if (addBtn && dot.dataset.variantId) {
            addBtn.dataset.variantId = dot.dataset.variantId;
          }

          const fallback = img ? img.dataset.fallbackImg : "";
          const newSrc = dot.dataset.img || fallback;
          if (img && newSrc && newSrc !== img.getAttribute("src")) {
            img.style.transition = "opacity 0.18s ease";
            img.style.opacity = "0";
            window.setTimeout(() => {
              img.src = newSrc;
              img.style.opacity = "1";
            }, 180);
          }
        });
      });
    });

    /* ---------- collection grid: chip filter (client-side show/hide, subtle fade) ---------- */
    const hasGSAP = window.gsap && window.ScrollTrigger;
    const chips = document.querySelectorAll(".chip[data-filter]");
    if (chips.length) {
      chips.forEach((chip) => {
        chip.addEventListener("click", () => {
          chips.forEach((c) => c.classList.remove("on"));
          chip.classList.add("on");
          const f = chip.dataset.filter;
          document.querySelectorAll("[data-cat]").forEach((card) => {
            card.classList.toggle("hide", f !== "all" && card.dataset.cat !== f);
          });
          if (hasGSAP) ScrollTrigger.refresh();
        });
      });
    }

    /* ---------- cart page: qty +/- and remove via /cart/change.js ---------- */
    const cartForm = document.querySelector("[data-bgc-cart-form]");
    if (cartForm) {
      function changeLine(line, quantity) {
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ line: line, quantity: quantity })
        })
          .then((r) => r.json())
          .then((cart) => {
            const row = cartForm.querySelector('[data-cart-line="' + line + '"]');
            if (quantity <= 0) {
              if (row) row.remove();
            } else if (row) {
              const item = cart.items[line - 1];
              if (item) {
                const totalEl = row.querySelector(".cart-row-total");
                if (totalEl) totalEl.textContent = formatMoney(item.final_line_price);
              }
            }
            const subtotalEl = cartForm.querySelector("[data-cart-subtotal]");
            if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
            if (cart.item_count === 0) {
              window.location.reload();
            }
            if (BGC.refreshCart) BGC.refreshCart();
            return cart;
          });
      }

      function formatMoney(cents) {
        return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "PKR", minimumFractionDigits: 0 })
          .replace("PKR", "Rs");
      }

      cartForm.querySelectorAll("[data-bgc-qty]").forEach((q) => {
        const line = parseInt(q.dataset.line, 10);
        const out = q.querySelector("[data-cart-qty]");
        q.querySelectorAll("button").forEach((b) => {
          b.addEventListener("click", () => {
            let n = parseInt(out.textContent, 10) || 1;
            n = Math.max(0, n + (b.dataset.d === "+" ? 1 : -1));
            out.textContent = n;
            changeLine(line, n);
          });
        });
      });

      cartForm.querySelectorAll("[data-bgc-remove]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const line = parseInt(btn.dataset.line, 10);
          changeLine(line, 0);
        });
      });
    }
  });
})();
